import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle2, XCircle, AlertTriangle, PlugZap } from "lucide-react";
import { mailboxApi } from "@/lib/api";

const PS = `Connect-ExchangeOnline
New-ServicePrincipal -AppId <App-ID> -ObjectId <Objekt-ID der Unternehmensanwendung> -DisplayName "R-Network CRM"
New-DistributionGroup -Name "CRM-Postfachzugriff" -Type Security -Members service@r-network.de
New-ManagementScope -Name "Nur Service-Postfach" -RecipientRestrictionFilter "MemberOfGroup -eq '<DN der Gruppe>'"
New-ManagementRoleAssignment -App <App-ID> -Role "Application Mail.Read" -CustomResourceScope "Nur Service-Postfach"`;

const Result = ({ t }) => (
  <div className="rounded-lg border p-3 text-sm space-y-1.5" data-testid="mailbox-graph-test-result">
    <p className="flex items-center gap-2">{t.mailbox_ok ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-destructive" />}
      {t.mailbox_ok ? "Lesezugriff auf service@r-network.de funktioniert" : t.error}</p>
    {t.mailbox_ok && (
      <p className="flex items-center gap-2">{t.restricted ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <AlertTriangle className="w-4 h-4 text-amber-600" />}
        {t.restricted ? "Zugriff ist auf dieses Postfach beschränkt" : t.restricted === false ? "Achtung: Die App kann auch andere Postfächer lesen – Beschränkung einrichten" : "Beschränkung konnte nicht geprüft werden (Test mit einem Konto mit eigenem Postfach wiederholen)"}</p>
    )}
    <p className="text-xs text-muted-foreground">Getestet {new Date(t.tested_at).toLocaleString("de-DE")}{t.credentials ? ` · Zugangsdaten ${t.credentials}` : ""}</p>
  </div>
);

export function MailboxConnectionCard({ toast }) {
  const [c, setC] = useState(null);
  const [f, setF] = useState({ tenant_id: "", client_id: "", client_secret: "" });
  const [busy, setBusy] = useState("");
  const load = () => mailboxApi.connection().then((r) => { setC(r.data); setF({ tenant_id: r.data.tenant_id, client_id: r.data.client_id, client_secret: "" }); });
  useEffect(() => { load().catch(() => {}); }, []);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); if (ok) toast.success(ok); await load(); } catch (e) { toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen"); } finally { setBusy(""); }
  };
  if (!c) return null;
  const graph = c.mode === "graph";
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  return (
    <Card className="mt-6" data-testid="mailbox-connection-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><PlugZap className="w-5 h-5" />Service-Postfach: Verbindungsart
          <Badge variant="outline" className={graph ? "border-green-300 bg-green-50 text-green-700" : "border-amber-300 bg-amber-50 text-amber-800"} data-testid="mailbox-connection-mode">{graph ? "Microsoft Graph" : "EWS (wird abgeschaltet)"}</Badge>
        </CardTitle>
        <CardDescription>Microsoft schaltet die alte EWS-Schnittstelle ab. Graph liest nur {c.mailbox} – mit reinem Lesezugriff.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <ol className="list-decimal pl-5 text-sm space-y-1 text-muted-foreground">
          <li>Azure-Portal → App-Registrierung → API-Berechtigungen → <b>Microsoft Graph · Anwendung · Mail.Read</b> hinzufügen und Administratorzustimmung erteilen.</li>
          <li>Zugriff in Exchange Online per PowerShell auf das Service-Postfach beschränken:</li>
        </ol>
        <pre className="text-xs bg-muted rounded-lg p-3 overflow-x-auto whitespace-pre" data-testid="mailbox-graph-powershell">{PS}</pre>
        <p className="text-sm text-muted-foreground">3. Optional eigene Zugangsdaten eintragen – leer lassen, um die bestehende Exchange-App-Registrierung zu verwenden.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1.5"><Label>Mandanten-ID</Label><Input value={f.tenant_id} onChange={set("tenant_id")} placeholder="wie Exchange" data-testid="mailbox-graph-tenant" /></div>
          <div className="space-y-1.5"><Label>App-ID</Label><Input value={f.client_id} onChange={set("client_id")} placeholder="wie Exchange" data-testid="mailbox-graph-client" /></div>
          <div className="space-y-1.5"><Label>Geheimer Schlüssel</Label><Input type="password" value={f.client_secret} onChange={set("client_secret")} placeholder={c.has_secret ? "gespeichert" : "wie Exchange"} autoComplete="new-password" data-testid="mailbox-graph-secret" /></div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" disabled={!!busy} onClick={() => run("save", () => mailboxApi.saveConnection(f), "Zugangsdaten gespeichert")} data-testid="mailbox-graph-save">{busy === "save" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Speichern</Button>
          <Button variant="outline" disabled={!!busy} onClick={() => run("test", () => mailboxApi.testConnection())} data-testid="mailbox-graph-test">{busy === "test" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Graph-Zugriff testen</Button>
          {graph
            ? <Button variant="ghost" disabled={!!busy} onClick={() => run("mode", () => mailboxApi.saveConnection({ mode: "ews" }), "Zurück auf EWS umgestellt")} data-testid="mailbox-switch-ews">Zurück auf EWS</Button>
            : <Button disabled={!!busy || !c.last_test?.mailbox_ok} onClick={() => run("mode", () => mailboxApi.saveConnection({ mode: "graph" }), "Auf Microsoft Graph umgestellt")} data-testid="mailbox-switch-graph">Auf Graph umstellen</Button>}
        </div>
        {c.last_test && <Result t={c.last_test} />}
      </CardContent>
    </Card>
  );
}
